import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';

function getPrimaryDate(festival) {
  return festival?.startDate ?? festival?.dates?.[0];
}

function formatDateTimeCell(festival) {
  const date = getPrimaryDate(festival);
  if (!date?.startTime) {
    return { dateLine: '—', timeLine: null };
  }
  const start = moment(date.startTime);
  const end = date.endTime ? moment(date.endTime) : null;
  return {
    dateLine: start.format('MMM D, YYYY'),
    timeLine: end
      ? `${start.format('h:mm A')} – ${end.format('h:mm A')}`
      : start.format('h:mm A'),
  };
}

function formatStatusSubtitle(status) {
  if (!status) {
    return 'Festival';
  }
  const label = String(status).replace(/_/g, ' ').toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function FestivalTable({ festivals, canModify = false }) {
  const setFestival = useFestivalWorkspaceStore((s) => s.setFestival);
  const total = festivals.length;

  return (
    <>
      <div className="manage-table-container">
        <table className="manage-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Event Name</th>
              <th>Date &amp; Time</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {total === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="py-10 text-center text-[var(--muted-foreground)]"
                >
                  {canModify
                    ? 'No events yet. Create your first event to get started.'
                    : 'No events yet.'}
                </td>
              </tr>
            ) : (
              festivals.map((festival) => {
                const { dateLine, timeLine } = formatDateTimeCell(festival);
                return (
                  <tr key={festival.festivalId}>
                    <td>
                      {festival.photoURL ? (
                        <img
                          src={festival.photoURL}
                          alt=""
                          className="event-thumb"
                        />
                      ) : (
                        <span
                          className="event-thumb inline-block bg-[var(--muted)]"
                          aria-hidden
                        />
                      )}
                    </td>
                    <td>
                      <strong className="text-[#121212]">
                        {festival.name}
                      </strong>
                      <br />
                      <small className="text-[var(--muted-foreground)]">
                        {formatStatusSubtitle(festival.status)}
                      </small>
                    </td>
                    <td>
                      {dateLine}
                      {timeLine && (
                        <>
                          <br />
                          <small className="text-[var(--muted-foreground)]">
                            {timeLine}
                          </small>
                        </>
                      )}
                    </td>
                    <td className="text-[var(--muted-foreground)]">—</td>
                    <td>
                      <div className="manage-actions">
                        <Link
                          to={`/festival/${festival.festivalId}/view`}
                          className="bethere-action-btn"
                          title="View"
                          aria-label={`View ${festival.name}`}
                          onClick={() => setFestival(festival)}
                        >
                          <FontAwesomeIcon icon={['fas', 'eye']} />
                        </Link>
                        {canModify && (
                          <Link
                            to={`/festival/${festival.festivalId}`}
                            className="bethere-action-btn"
                            title="Edit"
                            aria-label={`Edit ${festival.name}`}
                            onClick={() => setFestival(festival)}
                          >
                            <FontAwesomeIcon icon={['fas', 'pen-to-square']} />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="section-header-bethere mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="m-0 text-sm text-[var(--muted-foreground)]">
            Showing 1 to {total} of {total} Events
          </p>
        </div>
      )}
    </>
  );
}
